import React, { Suspense, useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { PageSkeleton } from "components/ui";
import { useNav } from "navigation/useNav";
import SideNav from "./SideNav";
import TopBar from "./TopBar";
import SafetyNotice from "./SafetyNotice";

const prefersReducedMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const AppShell = () => {
  const { pathname } = useLocation();
  const { role, user, sections, safety } = useNav();
  const [navOpen, setNavOpen] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    setNavOpen(false);
    // Animate without remounting, so pages keep their state across sub-routes.
    if (prefersReducedMotion() || !contentRef.current?.animate) return;
    contentRef.current.animate(
      [
        { opacity: 0, transform: "translateY(6px)" },
        { opacity: 1, transform: "none" },
      ],
      { duration: 220, easing: "cubic-bezier(0.22, 1, 0.36, 1)" }
    );
  }, [pathname]);

  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (e) => e.key === "Escape" && setNavOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [navOpen]);

  return (
    <div className="cc-shell">
      <SideNav
        sections={sections}
        role={role}
        user={user}
        open={navOpen}
        onClose={() => setNavOpen(false)}
      />
      <div
        className={`cc-scrim cc-nav-scrim${navOpen ? " is-open" : ""}`}
        onClick={() => setNavOpen(false)}
        aria-hidden="true"
      />
      <div className="cc-main">
        <TopBar sections={sections} user={user} onOpenNav={() => setNavOpen(true)} />
        <main className="cc-content" ref={contentRef}>
          {!/content-(review|safety)/.test(pathname) && <SafetyNotice summary={safety} />}
          <Suspense fallback={<PageSkeleton />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
};

export default AppShell;
